import { createContext, ReactNode, useCallback, useContext, useEffect, useRef, useState } from "react"
import { useLocation } from "react-router-dom"

import { ReactComponent as CheckIcon } from "assets/svg/icon-check.svg"
import { ReactComponent as ChevronIcon } from "assets/svg/icon-down.svg"

import {
  CollapsedGroupTrigger,
  MenuChevron,
  MenuGroupHeader,
  MenuIcon,
  MenuIconStroke,
  MenuSubItems,
  MenuText,
  PopoverCheck,
  PopoverDropdown,
  PopoverItem,
  SubNavLink,
} from "./styled"
import { useVerticalMenuContext } from "./VerticalMenu"

interface MenuItemGroupProps {
  icon: ReactNode
  label: string
  basePaths: string[]
  iconType?: "fill" | "stroke"
  children: ReactNode
}

interface SubMenuPopoverContextValue {
  inPopover: boolean
  closePopover: () => void
}

const SubMenuPopoverContext = createContext<SubMenuPopoverContextValue>({
  inPopover: false,
  closePopover: () => {},
})

export const MenuItemGroup = ({ icon, label, basePaths, iconType = "fill", children }: MenuItemGroupProps) => {
  const { isOpen } = useVerticalMenuContext()
  const { pathname } = useLocation()
  const IconWrapper = iconType === "stroke" ? MenuIconStroke : MenuIcon
  const isActive = basePaths.some(path => pathname === path || pathname.startsWith(`${path}/`))
  const [expanded, setExpanded] = useState(true)
  const [popoverOpen, setPopoverOpen] = useState(false)
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const triggerRef = useRef<HTMLDivElement>(null)
  const popoverRef = useRef<HTMLDivElement>(null)
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const cancelScheduledClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }, [])

  const openPopover = useCallback(() => {
    cancelScheduledClose()
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect()
      setPopoverPos({ top: rect.top, left: rect.right + 8 })
    }
    setPopoverOpen(true)
  }, [cancelScheduledClose])

  const closePopover = useCallback(() => {
    cancelScheduledClose()
    setPopoverOpen(false)
  }, [cancelScheduledClose])

  const scheduleClose = useCallback(() => {
    cancelScheduledClose()
    closeTimerRef.current = setTimeout(() => {
      setPopoverOpen(false)
      closeTimerRef.current = null
    }, 200)
  }, [cancelScheduledClose])

  useEffect(() => {
    if (isActive) {
      setExpanded(true)
    }
  }, [isActive])

  useEffect(() => {
    if (!popoverOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (popoverRef.current?.contains(target) || triggerRef.current?.contains(target)) return
      setPopoverOpen(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [popoverOpen])

  useEffect(() => {
    if (isOpen) setPopoverOpen(false)
  }, [isOpen])

  useEffect(() => cancelScheduledClose, [cancelScheduledClose])

  const isHoverPointer = (e: React.PointerEvent) => e.pointerType === "mouse" || e.pointerType === "pen"
  const handlePointerEnter = (e: React.PointerEvent) => {
    if (!isHoverPointer(e)) return
    openPopover()
  }
  const handlePointerLeave = (e: React.PointerEvent) => {
    if (!isHoverPointer(e)) return
    scheduleClose()
  }

  const togglePopover = () => {
    if (popoverOpen) {
      closePopover()
    } else {
      openPopover()
    }
  }

  if (!isOpen) {
    return (
      <>
        <CollapsedGroupTrigger
          ref={triggerRef}
          $active={isActive}
          onClick={togglePopover}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
        >
          <IconWrapper>{icon}</IconWrapper>
        </CollapsedGroupTrigger>
        {popoverOpen && (
          <PopoverDropdown
            ref={popoverRef}
            style={{ top: popoverPos.top, left: popoverPos.left }}
            onPointerEnter={handlePointerEnter}
            onPointerLeave={handlePointerLeave}
          >
            <SubMenuPopoverContext.Provider value={{ inPopover: true, closePopover }}>
              {children}
            </SubMenuPopoverContext.Provider>
          </PopoverDropdown>
        )}
      </>
    )
  }

  return (
    <>
      <MenuGroupHeader role="button" tabIndex={0} onClick={() => setExpanded(prev => !prev)}>
        <IconWrapper>{icon}</IconWrapper>
        <MenuText $isOpen={isOpen}>{label}</MenuText>
        <MenuChevron $expanded={expanded}>
          <ChevronIcon />
        </MenuChevron>
      </MenuGroupHeader>
      {expanded && <MenuSubItems>{children}</MenuSubItems>}
    </>
  )
}

interface MenuSubItemProps {
  to: string
  label: string
  target?: "_blank"
}

export const MenuSubItem = ({ to, label, target }: MenuSubItemProps) => {
  const { isOpen, closeMobileMenu } = useVerticalMenuContext()
  const { inPopover, closePopover } = useContext(SubMenuPopoverContext)

  if (inPopover) {
    return (
      <PopoverItem
        to={to}
        target={target}
        rel={target === "_blank" ? "noopener noreferrer" : undefined}
        end
        activeClassName="active"
        onClick={() => {
          window.scrollTo({ top: 0 })
          closeMobileMenu()
          closePopover()
        }}
      >
        <span>{label}</span>
        <PopoverCheck className="popover-check">
          <CheckIcon />
        </PopoverCheck>
      </PopoverItem>
    )
  }

  return (
    <SubNavLink
      to={to}
      target={target}
      rel={target === "_blank" ? "noopener noreferrer" : undefined}
      end
      activeClassName="active"
      onClick={() => {
        window.scrollTo({ top: 0 })
        closeMobileMenu()
      }}
    >
      <MenuText $isOpen={isOpen}>{label}</MenuText>
    </SubNavLink>
  )
}
